import Product from '@/models/loyaltyPanel/Product';
import _ from 'lodash';
const getDefaultState = () => ({
  showImageUpload: false,
  showWeekdays: false,
  categories: [],
  productsTags: [],
  products: [],
  product: new Product(),
  validationErrors: {},
});

export default {
  namespaced: true,

  state: () => getDefaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
    setShowImageUpload(state, payload) {
      state.showImageUpload = payload;
    },

    setShowWeekdays(state, payload) {
      state.showWeekdays = payload;
    },

    setCategories(state, payload) {
      state.categories = payload;
    },

    setProductsTags(state, payload) {
      state.productsTags = payload;
    },

    setItems(state, payload) {
      state.products = payload;
    },

    setItem(state, payload) {
      state.product = new Product(payload);
    },

    setValidationErrors(state, payload) {
      // Ensure validationErrors is always an object to avoid watcher errors
      state.validationErrors =
        payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    },

    addItem(state, payload) {
      state.products.unshift(payload);
    },

    updateItem(state, payload) {
      let index = state.products.findIndex((p) => p.product_id === payload.product_id);
      state.products.splice(index, 1, payload);
    },

    removeItem(state, id) {
      state.products = state.products.filter((p) => p.product_id !== id);
    },
  },

  actions: {
    async getCategories({ commit }) {
      try {
        const { data } = await Product.getCategories();

        const { product_categories } = data.data;
        commit('setCategories', product_categories);
      } catch (ex) {
        console.error(ex.response.data);
      }
    },

    async getProductsTags({ commit }) {
      try {
        const { data } = await Product.getProductsTags();

        const { product_tags } = data.data;
        commit('setProductsTags', product_tags);
      } catch (ex) {
        console.error(ex.response.data);
      }
    },

    async getItems({ commit }, query = '') {
      try {
        commit('setLoading', true, { root: true });

        const { data } = await Product.get(query);
        const { products, pagination } = data.data;

        commit('setItems', products);
        commit('setServerItemsLength', pagination.total, {
          root: true,
        });
        commit('setServerPagination', pagination, {
          root: true,
        });
        commit('setLoading', false, { root: true });
      } catch (ex) {
        commit('setLoading', false, { root: true });
        console.error(ex.response.data);
      }
    },

    async create({ commit, dispatch, state, rootState }, image) {
      try {
        commit('setLoading', true, { root: true });

        let product = { ...state.product };
        product.maximum_price_per_transaction = product.maximum_price_per_transaction || 0;

        delete product.product_id;
        delete product.image;

        if (!product.name.en) product.name.en = product.name.el;
        if (!product.name.it) product.name.it = product.name.el;
        if (!product.description.en) product.description.en = product.description.el;
        if (!product.description.it) product.description.it = product.description.el;

        const { data } = await Product.create(product);
        const createdProduct = data.data.product;

        commit('setItem', createdProduct);
        commit('setValidationErrors', {});

        if (image) {
          dispatch('uploadImage', {
            item: createdProduct,
            image,
            mode: 1,
          });
          createdProduct.has_image = true;
        } else {
          commit('addItem', createdProduct);
          commit('setServerItemsLength', rootState.serverItemsLength + 1, { root: true });
          commit('setLoading', false, { root: true });
          commit('setDialog', false, { root: true });
          commit(
            'setNotification',
            {
              show: true,
              type: 'success',
              text: 'You have successfully created product!',
            },
            { root: true }
          );
        }
      } catch (ex) {
        commit('setLoading', false, { root: true });
        // Store entire response data as validationErrors when available
        commit('setValidationErrors', ex?.response?.data || {});
        commit('setErrorMessage', ex?.response?.data?.message || '', { root: true });
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async update({ commit, dispatch, state, rootState }, image) {
      try {
        commit('setLoading', true, { root: true });

        let product = { ...state.product };
        product.maximum_price_per_transaction = product.maximum_price_per_transaction || 0;
        delete product.image;
        delete product.stores;
        delete product.group_product;

        if (!product.name.en) product.name.en = product.name.el;
        if (!product.name.it) product.name.it = product.name.el;
        if (!product.description.en) product.description.en = product.description.el;
        if (!product.description.it) product.description.it = product.description.el;
        const { data } = await Product.update(product);

        if (image) {
          dispatch('uploadImage', {
            item: data.data.product,
            image,
            mode: 2,
          });
        } else {
          commit('updateItem', data.data.product);
          commit('setValidationErrors', {});
          commit('setLoading', false, { root: true });
          commit('setDialog', false, { root: true });
          commit(
            'setNotification',
            {
              show: true,
              type: 'success',
              text: 'You have successfully updated product!',
            },

            { root: true }
          );
        }
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit('setValidationErrors', ex?.response?.data || {});
        commit('setErrorMessage', ex?.response?.data?.message || '', { root: true });
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async remove({ commit, state, rootState }) {
      try {
        commit('setLoading', true, { root: true });

        await Product.delete(state.product.product_id);

        commit('setServerItemsLength', rootState.serverItemsLength - 1, { root: true });
        commit('setLoading', false, { root: true });
        commit('setDeleteDialog', false, { root: true });
        commit('removeItem', state.product.product_id);
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully deleted product!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit('setErrorMessage', ex.response.data.message, {
          root: true,
        });
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async uploadImage({ commit, rootState }, { item, image, mode }) {
      try {
        const fd = new FormData();
        fd.append('image', image);

        const { data } = await Product.uploadImage(item.product_id, fd);

        item.image = data.data.image;

        if (mode === 1) {
          commit('addItem', item);
          commit('setServerItemsLength', rootState.serverItemsLength + 1, { root: true });
        } else {
          commit('updateItem', item);
        }
        commit('setLoading', false, { root: true });
        commit('setDialog', false, { root: true });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: `You have successfully ${mode === 1 ? 'created' : 'updated'} product!`,
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        const errorMessage = ex?.response?.data?.message || 'Image upload failed.';

        // If create succeeded but image failed, keep editing the same product.
        if (mode === 1 && item?.product_id) {
          commit('setItem', item);
        }

        commit('setErrorMessage', errorMessage, {
          root: true,
        });
        commit(
          'setNotification',
          {
            show: true,
            type: 'error',
            text: errorMessage,
          },
          { root: true }
        );
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async uploadProductsCsv({ commit, rootState }, file) {
      try {
        commit('setLoading', true, { root: true });

        const fd = new FormData();
        fd.append('products', file);

        const res = await Product.uploadProductsCsv(fd);

        commit('setLoading', false, { root: true });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: {
              en: `You have successfully uploaded the file!`,
              el: `Ανεβάσατε το αρχείο επιτυχώς!`,
              it: `Il file è stato caricato con successo!`,
            }[res.headers['X-Locale'] || 'en'],
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit('setErrorMessage', ex.response.data.message, {
          root: true,
        });
        commit(
          'setNotification',
          {
            show: true,
            type: 'error',
            text: ex.response.data.message,
          },

          { root: true }
        );
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async updateByTags({ commit, dispatch, state, rootState }) {
      try {
        commit('setLoading', true, { root: true });

        let fields = ['retail_price', 'wholesale_price', 'reward_points', 'add_to_stores'];
        let body = _.pick(state.product, ...fields);
        let tagId = state.product.product_tag_id;

        const { data } = await Product.updateByTags(tagId, body);

        await dispatch('getItems');
        commit('setLoading', false, { root: true });
        commit('setDialog', false, { root: true });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated products!',
          },
          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit('setValidationErrors', ex?.response?.data || {});
        commit('setErrorMessage', ex?.response?.data?.message || '', { root: true });
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },

    async updateBulk({ commit, dispatch, state, rootState }, ids) {
      try {
        commit('setLoading', true, { root: true });

        let fields = ['retail_price', 'wholesale_price', 'reward_points', 'add_to_stores'];
        let body = _.pick(state.product, ...fields);
        let tagId = state.product.product_tag_id;
        if (ids?.length) {
          body.product_tag_id = tagId;
          body.product_ids = ids;
          tagId = '';
        }

        const { data } = await Product.updateBulk(tagId, body);

        await dispatch('getItems');
        commit('setLoading', false, { root: true });
        commit('setDialog', false, { root: true });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated products!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit(
          'setValidationErrors',
          ex?.response?.data?.errors || ex?.response?.data?.error || {}
        );
        commit('setErrorMessage', ex?.response?.data?.message || '', {
          root: true,
        });
        setTimeout(() => commit('setErrorMessage', '', { root: true }), 5000);
      }
    },
  },
};
